import { useMemo } from 'react';
import { arDunningApi } from '../../services/arDunningApi';
import { CommentsThread, type CommentsAdapter } from '../ui/CommentsThread';
import type { ArEntityType } from '../../types/arDunning';

interface Props {
    entityType: ArEntityType;
    entityRef: string;
    canEdit: boolean;
    /** Fired after a comment is added/edited/deleted so list badges can refresh. */
    onChanged?: () => void;
}

/**
 * AR-Dunning binding of the generic CommentsThread: one open item or customer, bound to
 * the ar-dunning comments endpoint (EntityType + EntityRef).
 *
 * The UI itself lives in components/ui/CommentsThread.tsx and is shared with the
 * consolidated-business drawer and the Customer-360 Lost-Business tab.
 */
export function ArCommentsThread({ entityType, entityRef, canEdit, onChanged }: Props) {
    // Memoised: CommentsThread's load effect depends on the adapter identity.
    const adapter = useMemo<CommentsAdapter>(() => ({
        list: async () => (await arDunningApi.listComments(entityType, entityRef)).comments,
        create: text => arDunningApi.createComment(entityType, entityRef, text),
        update: (id, text) => arDunningApi.updateComment(id, text),
        remove: id => arDunningApi.deleteComment(id),
    }), [entityType, entityRef]);

    return <CommentsThread adapter={adapter} canEdit={canEdit} onChanged={onChanged} />;
}
